import { Message, MessageContent, MessageHeader } from "@/components/ui/message"
import { cn } from "@/lib/utils"
import type { ThreadMessage } from "@/types/messaging"

import { RolePill } from "./role-pill"

/**
 * OKS-37 / OKS-654 — One chat bubble, rendered on the shadcn `Message`
 * primitive (OKS-654 migration). `align="end"` + accent fill when
 * `mine`, `align="start"` + neutral fill otherwise — the primitive's
 * `data-[align=end]:flex-row-reverse` drives the row direction.
 *
 * **Security (XSS posture):** body is rendered via JSX text
 * interpolation (`{message.body}`), so React escapes any HTML
 * characters. A `<script>`-laden body renders as literal text — no
 * `<script>` element is added to the document. Asserted by
 * `spec/system/brand/messaging_xss_spec.rb` (system) + a Vitest unit
 * snapshot. The `.message-bubble` class + the
 * `messaging-message-bubble-{id}` test id are preserved across the
 * OKS-654 primitive swap.
 *
 * OKS-763 — On the `mine` (orange `bg-primary`) bubble the header
 * metadata (sender name, role pill, timestamp) previously read gray:
 * the name/timestamp were dimmed with `opacity-90`/`opacity-70` and
 * the role pill was a fixed neutral `secondary` badge. The dimming is
 * now gated on `!message.mine` — mine renders the name AND timestamp
 * at full white (both hit the 3.22:1 body-text baseline, the ceiling
 * for white-on-`--primary`; hierarchy is carried by the timestamp's
 * `text-[10px]` size, not opacity), while `onPrimary={message.mine}`
 * gives the pill a border-only white treatment. Incoming (`bg-muted`)
 * bubbles keep the exact prior treatment. Styling/props only — no DOM
 * restructure.
 */
interface ThreadMessageBubbleProps {
  message: ThreadMessage
}

export function ThreadMessageBubble({ message }: ThreadMessageBubbleProps) {
  const time = new Date(message.createdAt)
  const timeLabel = time.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })

  return (
    <Message
      align={message.mine ? "end" : "start"}
      data-testid={`messaging-message-bubble-${message.id}`}
    >
      <MessageContent
        // `w-auto` neutralises the primitive's `w-full` so the bubble
        // hugs its content up to 80% (preserves the shipped OKS-37
        // look); `gap-0.5` overrides the primitive's `gap-2.5`.
        className={cn(
          "w-auto max-w-[80%] gap-0.5 rounded-lg px-3 py-2 text-sm",
          message.mine
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
        aria-label={`Message from ${message.senderName} at ${timeLabel}: ${message.body}`}
      >
        <MessageHeader className="mb-0.5 flex items-baseline gap-1.5">
          <span
            className={cn("text-xs font-medium", !message.mine && "opacity-90")}
          >
            {message.senderName}
          </span>
          <RolePill
            role={message.senderRoleSnapshot}
            onPrimary={message.mine}
          />
          <time
            dateTime={message.createdAt}
            className={cn("ml-auto text-[10px]", !message.mine && "opacity-70")}
          >
            {timeLabel}
          </time>
        </MessageHeader>
        <p className="message-bubble break-words whitespace-pre-wrap">
          {message.body}
        </p>
      </MessageContent>
    </Message>
  )
}
